import openai, { OPENAI_MODELS } from './openaiClient';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with isInternal flag and message.
 */
function getErrorMessage(error) {
  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits or do not have access to this resource.' };
  } else if (error instanceof RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your API key and internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Calculates distance between two locations using Haversine formula.
 * @param {Object} loc1 - First location with lat/lng.
 * @param {Object} loc2 - Second location with lat/lng.
 * @returns {number} Distance in kilometers.
 */
function calculateDistance(loc1, loc2) {
  const R = 6371; // Earth's radius in km
  const dLat = (loc2?.lat - loc1?.lat) * Math.PI / 180;
  const dLon = (loc2?.lng - loc1?.lng) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(loc1?.lat * Math.PI / 180) * Math.cos(loc2?.lat * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

/**
 * Generates AI-powered activity recommendations based on user preferences and context.
 * @param {Object} params - Recommendation parameters.
 * @returns {Promise<Object>} Recommended activities with reasoning.
 */
export async function generateActivityRecommendations(params) {
  const { 
    destination, 
    travelDates, 
    userPreferences, 
    currentItinerary, 
    availableActivities,
    budget,
    travelers 
  } = params;

  // Calculate trip duration in days
  const startDate = new Date(travelDates?.startDate);
  const endDate = new Date(travelDates?.endDate);
  const tripDuration = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  // Calculate current spending
  let currentSpending = 0;
  currentItinerary?.forEach(day => {
    day?.timeSlots?.forEach(slot => {
      if (slot?.activity) {
        currentSpending += slot?.activity?.cost || 0;
      }
    });
    if (day?.accommodation) {
      currentSpending += day?.accommodation?.cost || 0;
    }
  });

  const remainingBudget = budget - currentSpending;
  const budgetPerDay = remainingBudget / tripDuration;

  // Count activities already scheduled
  let scheduledActivitiesCount = 0;
  currentItinerary?.forEach(day => {
    day?.timeSlots?.forEach(slot => {
      if (slot?.activity) scheduledActivitiesCount++;
    });
  });

  try {
    const response = await openai?.chat?.completions?.create({
      model: OPENAI_MODELS?.FLAGSHIP,
      messages: [
        { 
          role: 'system', 
          content: `You are an expert travel recommendation AI specializing in personalized activity suggestions. Your goal is to:
1. Analyze user budget constraints and suggest activities that fit within their remaining budget
2. Match activities to user interests with high precision
3. Optimize recommendations based on trip duration (suggest appropriate number of activities per day)
4. Consider time of day, weather, and logical activity flow
5. Provide clear reasoning for each recommendation
6. Balance must-see attractions with hidden gems based on user preferences

Always prioritize budget awareness, interest alignment, and realistic time management.` 
        },
        { 
          role: 'user', 
          content: `Generate smart activity recommendations for this trip:

**Trip Overview:**
- Destination: ${destination}
- Travel Dates: ${travelDates?.startDate} to ${travelDates?.endDate}
- Trip Duration: ${tripDuration} days
- Number of Travelers: ${travelers}

**Budget Analysis:**
- Total Budget: PHP ${budget?.toLocaleString()}
- Already Spent: PHP ${currentSpending?.toLocaleString()}
- Remaining Budget: PHP ${remainingBudget?.toLocaleString()}
- Budget per Day: PHP ${budgetPerDay?.toFixed(0)}
- Activities Already Scheduled: ${scheduledActivitiesCount}

**User Interests & Preferences:**
${JSON.stringify(userPreferences, null, 2)}

**Current Itinerary Status:**
${JSON.stringify(currentItinerary?.map(day => ({
  day: day?.day,
  date: day?.date,
  scheduledActivities: day?.timeSlots?.filter(slot => slot?.activity)?.length,
  activities: day?.timeSlots?.filter(slot => slot?.activity)?.map(slot => slot?.activity?.title)
})), null, 2)}

**Available Activities to Choose From:**
${JSON.stringify(availableActivities?.map(act => ({
  id: act?.id,
  title: act?.title,
  category: act?.category,
  duration: act?.duration,
  cost: act?.cost,
  description: act?.description
})), null, 2)}

**Your Task:**
Provide 5-8 highly personalized recommendations that:
1. Stay within the remaining budget (PHP ${remainingBudget?.toLocaleString()})
2. Strongly match user interests: ${userPreferences?.interests?.join(', ')}
3. Are appropriately distributed across ${tripDuration} days
4. Consider optimal timing and activity sequencing
5. Include a mix of popular and unique experiences
6. Account for activity duration and travel time

For each recommendation, provide:
- Match score (0-100) based on interests, budget fit, and timing
- Clear reasoning explaining why this activity suits the user
- Specific day and time suggestions
- Budget impact analysis
- Proximity and logistics considerations` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'activity_recommendations_response',
          schema: {
            type: 'object',
            properties: {
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    activityId: { type: 'string' },
                    title: { type: 'string' },
                    category: { type: 'string' },
                    matchScore: { type: 'number' },
                    reasoning: { type: 'string' },
                    suggestedDay: { type: 'number' },
                    suggestedTime: { type: 'string' },
                    estimatedDuration: { type: 'number' },
                    estimatedCost: { type: 'number' },
                    proximityScore: { type: 'number' },
                    preferenceMatch: { type: 'array', items: { type: 'string' } },
                    bestTimeToVisit: { type: 'string' },
                    weatherConsideration: { type: 'string' },
                    crowdLevel: { type: 'string' },
                    budgetImpact: { type: 'string' },
                    interestAlignment: { type: 'string' }
                  },
                  required: ['activityId', 'title', 'category', 'matchScore', 'reasoning', 'suggestedDay', 'suggestedTime', 'estimatedDuration', 'estimatedCost', 'proximityScore', 'preferenceMatch', 'bestTimeToVisit', 'weatherConsideration', 'crowdLevel', 'budgetImpact', 'interestAlignment']
                }
              },
              optimizationSuggestions: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string' },
                    suggestion: { type: 'string' },
                    impact: { type: 'string' }
                  },
                  required: ['type', 'suggestion', 'impact']
                }
              },
              budgetAnalysis: {
                type: 'object',
                properties: {
                  totalEstimated: { type: 'number' },
                  remainingBudget: { type: 'number' },
                  budgetUtilization: { type: 'number' },
                  savingOpportunities: { type: 'array', items: { type: 'string' } },
                  budgetWarnings: { type: 'array', items: { type: 'string' } }
                },
                required: ['totalEstimated', 'remainingBudget', 'budgetUtilization', 'savingOpportunities', 'budgetWarnings']
              },
              tripInsights: {
                type: 'object',
                properties: {
                  pacingRecommendation: { type: 'string' },
                  interestCoverage: { type: 'string' },
                  durationOptimization: { type: 'string' },
                  balanceScore: { type: 'number' }
                },
                required: ['pacingRecommendation', 'interestCoverage', 'durationOptimization', 'balanceScore']
              }
            },
            required: ['recommendations', 'optimizationSuggestions', 'budgetAnalysis', 'tripInsights'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'high',
      temperature: 0.8,
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating recommendations:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Optimizes itinerary sequence based on location proximity and time efficiency.
 * @param {Object} params - Optimization parameters.
 * @returns {Promise<Object>} Optimized itinerary with routing suggestions.
 */
export async function optimizeItinerarySequence(params) {
  const { 
    currentItinerary, 
    destination,
    travelDates,
    preferences 
  } = params;

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are an expert itinerary optimizer. Analyze activity sequences and suggest optimal ordering based on location proximity, time efficiency, opening hours, and logical flow. Minimize travel time and maximize experience quality.' 
        },
        { 
          role: 'user', 
          content: `Optimize this itinerary:
- Destination: ${destination}
- Travel Dates: ${travelDates?.startDate} to ${travelDates?.endDate}
- Current Itinerary: ${JSON.stringify(currentItinerary)}
- User Preferences: ${JSON.stringify(preferences)}

Provide optimized sequence with routing logic, time savings, and reasoning.` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'itinerary_optimization_response',
          schema: {
            type: 'object',
            properties: {
              optimizedItinerary: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    day: { type: 'number' },
                    activities: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          activityId: { type: 'string' },
                          title: { type: 'string' },
                          suggestedTime: { type: 'string' },
                          duration: { type: 'number' },
                          travelTimeToNext: { type: 'number' },
                          reasoning: { type: 'string' }
                        },
                        required: ['activityId', 'title', 'suggestedTime', 'duration', 'travelTimeToNext', 'reasoning']
                      }
                    }
                  },
                  required: ['day', 'activities']
                }
              },
              improvements: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    type: { type: 'string' },
                    description: { type: 'string' },
                    timeSaved: { type: 'number' },
                    benefit: { type: 'string' }
                  },
                  required: ['type', 'description', 'timeSaved', 'benefit']
                }
              },
              routingInsights: {
                type: 'object',
                properties: {
                  totalTravelTime: { type: 'number' },
                  averageDailyDistance: { type: 'number' },
                  efficiencyScore: { type: 'number' },
                  suggestions: { type: 'array', items: { type: 'string' } }
                },
                required: ['totalTravelTime', 'averageDailyDistance', 'efficiencyScore', 'suggestions']
              }
            },
            required: ['optimizedItinerary', 'improvements', 'routingInsights'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'high',
      verbosity: 'medium',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error optimizing itinerary:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes user preferences from profile and behavior.
 * @param {Object} userProfile - User profile data.
 * @param {Array} pastTrips - User's past trip history.
 * @returns {Object} Analyzed preferences with weights.
 */
export function analyzeUserPreferences(userProfile, pastTrips = []) {
  const preferences = {
    interests: [],
    budgetRange: 'medium',
    travelStyle: 'balanced',
    activityLevel: 'moderate',
    preferredCategories: []
  };

  // Extract interests from profile
  if (userProfile?.interests) {
    preferences.interests = userProfile?.interests;
  }

  // Analyze past trips for patterns
  if (pastTrips?.length > 0) {
    const categoryCount = {};
    pastTrips?.forEach(trip => {
      trip?.activities?.forEach(activity => {
        categoryCount[activity.category] = (categoryCount?.[activity?.category] || 0) + 1;
      });
    });

    // Sort categories by frequency
    preferences.preferredCategories = Object.entries(categoryCount)?.sort((a, b) => b?.[1] - a?.[1])?.map(([category]) => category);
  }

  return preferences;
}

/**
 * Filters activities by location proximity to optimize travel time.
 * @param {Array} activities - Available activities.
 * @param {Object} referenceLocation - Current location or hotel.
 * @param {number} maxDistance - Maximum distance in km.
 * @returns {Array} Filtered activities sorted by proximity.
 */
export function filterByProximity(activities, referenceLocation, maxDistance = 50) {
  if (!referenceLocation?.lat || !referenceLocation?.lng) {
    return activities;
  }

  return activities?.map(activity => {
      const distance = calculateDistance(referenceLocation, {
        lat: activity?.latitude || 0,
        lng: activity?.longitude || 0
      });
      return { ...activity, distance, proximityScore: Math.max(0, 100 - (distance / maxDistance * 100)) };
    })?.filter(activity => activity?.distance <= maxDistance)?.sort((a, b) => a?.distance - b?.distance);
}

/**
 * Calculates optimal time slots for activities based on various factors.
 * @param {Object} activity - Activity details.
 * @param {Object} context - Context including weather, crowd patterns, etc.
 * @returns {Array} Suggested time slots with scores.
 */
export function calculateOptimalTimeSlots(activity, context) {
  const timeSlots = [
    { time: '08:00 AM', score: 0, reasons: [] },
    { time: '10:00 AM', score: 0, reasons: [] },
    { time: '12:00 PM', score: 0, reasons: [] },
    { time: '02:00 PM', score: 0, reasons: [] },
    { time: '04:00 PM', score: 0, reasons: [] },
    { time: '06:00 PM', score: 0, reasons: [] },
    { time: '08:00 PM', score: 0, reasons: [] }
  ];

  // Score based on activity category
  if (activity?.category === 'sightseeing') {
    timeSlots[0].score += 30; // Early morning
    timeSlots?.[0]?.reasons?.push('Best lighting for photos');
    timeSlots[4].score += 25; // Late afternoon
    timeSlots?.[4]?.reasons?.push('Golden hour views');
  } else if (activity?.category === 'food') {
    timeSlots[2].score += 40; // Lunch time
    timeSlots?.[2]?.reasons?.push('Peak dining hours');
    timeSlots[5].score += 35; // Dinner time
    timeSlots?.[5]?.reasons?.push('Evening dining experience');
  } else if (activity?.category === 'activity') {
    timeSlots[1].score += 35; // Mid-morning
    timeSlots?.[1]?.reasons?.push('Optimal energy levels');
    timeSlots[3].score += 30; // Afternoon
    timeSlots?.[3]?.reasons?.push('Good weather conditions');
  } else if (activity?.category === 'entertainment') {
    timeSlots[5].score += 40; // Evening
    timeSlots?.[5]?.reasons?.push('Prime entertainment hours');
    timeSlots[6].score += 35; // Night
    timeSlots?.[6]?.reasons?.push('Vibrant nightlife');
  }

  // Weather considerations
  if (context?.weather === 'hot') {
    timeSlots[0].score += 20; // Early morning
    timeSlots[6].score += 15; // Evening
  }

  return timeSlots?.sort((a, b) => b?.score - a?.score);
}

/**
 * Generates personalized destination suggestions using OpenAI based on user preferences and booking history.
 * @param {Object} params - Parameters for generating recommendations.
 * @returns {Promise<Object>} Personalized destination recommendations with reasoning.
 */
export async function generatePersonalizedDestinations(params) {
  const { 
    userProfile, 
    bookingHistory, 
    availableDestinations,
    preferences 
  } = params;

  // Analyze booking patterns
  const bookingPatterns = analyzeBookingPatterns(bookingHistory);
  
  try {
    const response = await openai?.chat?.completions?.create({
      model: OPENAI_MODELS?.FLAGSHIP,
      messages: [
        { 
          role: 'system', 
          content: `You are an expert travel recommendation AI specializing in personalized destination suggestions. Your goal is to:
1. Analyze user travel history and preferences to understand their travel personality
2. Identify patterns in past bookings (destination types, budget ranges, travel styles)
3. Suggest destinations that match their interests while introducing new experiences
4. Balance familiar preferences with discovery opportunities
5. Consider seasonal factors, budget constraints, and travel trends
6. Provide compelling reasons for each recommendation with personalization

Always prioritize user satisfaction, budget awareness, and authentic travel experiences.` 
        },
        { 
          role: 'user', 
          content: `Generate personalized destination recommendations for this user:

**User Profile:**
${JSON.stringify(userProfile, null, 2)}

**Booking History Analysis:**
- Total Bookings: ${bookingHistory?.length || 0}
- Favorite Destination Types: ${bookingPatterns?.favoriteTypes?.join(', ') || 'None yet'}
- Average Budget Range: PHP ${bookingPatterns?.avgBudget?.toLocaleString() || '0'}
- Travel Frequency: ${bookingPatterns?.frequency || 'New traveler'}
- Preferred Travel Style: ${bookingPatterns?.travelStyle || 'Exploring'}
- Past Destinations: ${bookingPatterns?.pastDestinations?.join(', ') || 'None yet'}

**User Preferences:**
${JSON.stringify(preferences, null, 2)}

**Available Destinations:**
${JSON.stringify(availableDestinations?.map(dest => ({
  id: dest?.id,
  name: dest?.name,
  location: dest?.location,
  category: dest?.category,
  priceRange: dest?.priceRange,
  rating: dest?.rating,
  description: dest?.description,
  highlights: dest?.highlights
})), null, 2)}

**Your Task:**
Provide 6-8 highly personalized destination recommendations that:
1. Align with user's proven interests from booking history
2. Match their budget patterns and travel style
3. Introduce new experiences that complement their preferences
4. Consider seasonal opportunities and current travel trends
5. Balance popular destinations with hidden gems
6. Provide compelling, personalized reasons for each suggestion

For each recommendation, provide:
- Personalization score (0-100) based on booking history match
- Detailed reasoning explaining why this destination suits them
- Connection to their past travel patterns
- What makes this recommendation unique for them
- Budget fit analysis
- Best time to visit based on their preferences` 
        },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'personalized_destinations_response',
          schema: {
            type: 'object',
            properties: {
              recommendations: {
                type: 'array',
                items: {
                  type: 'object',
                  properties: {
                    destinationId: { type: 'string' },
                    name: { type: 'string' },
                    location: { type: 'string' },
                    personalizationScore: { type: 'number' },
                    reasoning: { type: 'string' },
                    connectionToHistory: { type: 'string' },
                    uniqueAppeal: { type: 'string' },
                    budgetFit: { type: 'string' },
                    bestTimeToVisit: { type: 'string' },
                    estimatedBudget: { type: 'number' },
                    tripDuration: { type: 'string' },
                    highlights: { type: 'array', items: { type: 'string' } },
                    matchedInterests: { type: 'array', items: { type: 'string' } },
                    travelStyle: { type: 'string' },
                    experienceLevel: { type: 'string' },
                    seasonalAdvantage: { type: 'string' }
                  },
                  required: ['destinationId', 'name', 'location', 'personalizationScore', 'reasoning', 'connectionToHistory', 'uniqueAppeal', 'budgetFit', 'bestTimeToVisit', 'estimatedBudget', 'tripDuration', 'highlights', 'matchedInterests', 'travelStyle', 'experienceLevel', 'seasonalAdvantage']
                }
              },
              travelPersonality: {
                type: 'object',
                properties: {
                  primaryStyle: { type: 'string' },
                  budgetProfile: { type: 'string' },
                  adventureLevel: { type: 'string' },
                  culturalInterest: { type: 'string' },
                  travelFrequency: { type: 'string' }
                },
                required: ['primaryStyle', 'budgetProfile', 'adventureLevel', 'culturalInterest', 'travelFrequency']
              },
              insights: {
                type: 'object',
                properties: {
                  bookingPatterns: { type: 'string' },
                  preferenceEvolution: { type: 'string' },
                  recommendationStrategy: { type: 'string' },
                  discoveryOpportunities: { type: 'array', items: { type: 'string' } }
                },
                required: ['bookingPatterns', 'preferenceEvolution', 'recommendationStrategy', 'discoveryOpportunities']
              }
            },
            required: ['recommendations', 'travelPersonality', 'insights'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'high',
      temperature: 0.8,
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    const errorInfo = getErrorMessage(error);
    if (errorInfo?.isInternal) {
      console.log(errorInfo?.message);
    } else {
      console.error('Error generating personalized destinations:', error);
    }
    throw new Error(errorInfo.message);
  }
}

/**
 * Analyzes booking history to identify patterns and preferences.
 * @param {Array} bookingHistory - User's past bookings.
 * @returns {Object} Analyzed patterns and insights.
 */
function analyzeBookingPatterns(bookingHistory = []) {
  if (!bookingHistory || bookingHistory?.length === 0) {
    return {
      favoriteTypes: [],
      avgBudget: 0,
      frequency: 'New traveler',
      travelStyle: 'Exploring',
      pastDestinations: []
    };
  }

  // Calculate average budget
  const totalSpent = bookingHistory?.reduce((sum, booking) => sum + (booking?.amount || 0), 0);
  const avgBudget = totalSpent / bookingHistory?.length;

  // Extract destination types
  const typeCount = {};
  const destinations = [];
  
  bookingHistory?.forEach(booking => {
    const type = booking?.type || 'other';
    typeCount[type] = (typeCount?.[type] || 0) + 1;
    
    if (booking?.location && !destinations?.includes(booking?.location)) {
      destinations?.push(booking?.location);
    }
  });

  // Sort types by frequency
  const favoriteTypes = Object.entries(typeCount)
    ?.sort((a, b) => b?.[1] - a?.[1])
    ?.map(([type]) => type);

  // Determine travel frequency
  let frequency = 'Occasional traveler';
  if (bookingHistory?.length >= 10) frequency = 'Frequent traveler';
  else if (bookingHistory?.length >= 5) frequency = 'Regular traveler';
  else if (bookingHistory?.length >= 2) frequency = 'Occasional traveler';
  else frequency = 'New traveler';

  // Determine travel style based on booking types
  let travelStyle = 'Balanced explorer';
  if (favoriteTypes?.[0] === 'accommodation') travelStyle = 'Comfort-focused';
  else if (favoriteTypes?.[0] === 'activity') travelStyle = 'Adventure seeker';
  else if (avgBudget > 20000) travelStyle = 'Luxury traveler';
  else if (avgBudget < 5000) travelStyle = 'Budget explorer';

  return {
    favoriteTypes,
    avgBudget,
    frequency,
    travelStyle,
    pastDestinations: destinations
  };
}